import React, { useState } from 'react';
import { Package, Plus, Edit, Trash2, Search, Filter, DollarSign, Tag, Grid, List } from 'lucide-react';
import { formatDZD } from '../utils/currency';
import { useProducts } from '../contexts/ProductsContext';
import { useCategories } from '../contexts/CategoriesContext';
import './Products.css';

const Products = () => {
  const { 
    products, 
    addProduct, 
    updateProduct, 
    deleteProduct
  } = useProducts();
  
  const { categories } = useCategories();

  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'product',
    category: '',
    price: '',
    unit: 'unité',
    sku: '',
    stock: '',
    status: 'active'
  });

  const units = ['unité', 'forfait', 'heure', 'jour', 'session', 'kg', 'mètre', 'litre'];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || product.type === filterType;
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingProduct) {
      updateProduct(editingProduct.id, {
        ...formData,
        price: parseFloat(formData.price),
        stock: formData.type === 'service' ? null : parseInt(formData.stock) || 0
      });
    } else {
      addProduct({
        ...formData,
        price: parseFloat(formData.price),
        stock: formData.type === 'service' ? null : parseInt(formData.stock) || 0
      });
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'product',
      category: '',
      price: '',
      unit: 'unité',
      sku: '',
      stock: '',
      status: 'active'
    });
    setEditingProduct(null);
    setShowModal(false);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    if (product) {
      // Editing existing product
      setFormData({
        name: product.name,
        description: product.description,
        type: product.type,
        category: product.category,
        price: product.price.toString(),
        unit: product.unit,
        sku: product.sku,
        stock: product.stock ? product.stock.toString() : '',
        status: product.status
      });
    } else {
      // Creating new product - set default values
      setFormData({
        name: '',
        description: '',
        type: 'product',
        category: '',
        price: '',
        unit: 'unité',
        sku: '',
        stock: '',
        status: 'active'
      });
    }
    
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit/service ?')) {
      deleteProduct(id);
    }
  };

  const stats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    services: products.filter(p => p.type === 'service').length,
    products: products.filter(p => p.type === 'product').length,
    totalValue: products.reduce((sum, p) => sum + (p.price * (p.stock || 1)), 0)
  };

  return (
    <div className="products fade-in">
      <div className="page-header">
        <h1 className="page-title" style={{ color: '#ffffff' }}>
          <Package size={32} />
          Produits & Services
        </h1>
        <p className="page-subtitle" style={{ color: '#ffffff' }}>Gérez votre catalogue de produits et services</p>
      </div>

      {/* Statistics */}
      <div className="products-stats">
        <div className="stat-item">
          <div className="stat-icon">
            <Package size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon active">
            <Package size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.active}</span>
            <span className="stat-label">Actifs</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon service">
            <Tag size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.services}</span>
            <span className="stat-label">Services</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon product">
            <Package size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{stats.products}</span>
            <span className="stat-label">Produits</span>
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-icon value">
            <DollarSign size={24} />
          </div>
          <div className="stat-info">
            <span className="stat-number">{formatDZD(stats.totalValue)}</span>
            <span className="stat-label">Valeur totale</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="products-toolbar">
        <div className="search-filters">
          <div className="search-input">
            <Search size={20} />
            <input
              type="text"
              placeholder="Rechercher un produit/service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous types</option>
            <option value="product">Produits</option>
            <option value="service">Services</option>
          </select>
          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">Toutes catégories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="toolbar-right">
          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Vue cartes"
            >
              <Grid size={18} />
            </button>
            <button
              className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vue tableau"
            >
              <List size={18} />
            </button>
          </div>

          <button 
            className="btn-primary"
            onClick={() => handleEdit(null)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              padding: '0.75rem 1rem'
            }}
          >
            <Plus size={16} />
            <span>Nouveau produit/service</span>
          </button>
        </div>
      </div>

      {/* Products Display */}
      {viewMode === 'table' ? (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>Produit/Service</th>
                <th>Type</th>
                <th>Catégorie</th>
                <th>Prix</th>
                <th>Stock</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <tr key={product.id}>
                    <td className="product-info-cell">
                      <div className="product-name">{product.name}</div>
                      <div className="product-sku">SKU: {product.sku}</div>
                      <div className="product-description">{product.description}</div>
                    </td>
                    <td>
                      <span className={`type-badge ${product.type}`}>
                        {product.type === 'product' ? 'Produit' : 'Service'}
                      </span>
                    </td>
                    <td>{product.category}</td>
                    <td className="price-cell">
                      <div className="price">{formatDZD(product.price)}</div>
                      <div className="unit">/{product.unit}</div>
                    </td>
                    <td className="stock-cell">
                      {product.type === 'service' ? (
                        <span className="stock-unlimited">∞</span>
                      ) : (
                        <span className={`stock ${product.stock <= 5 ? 'low' : ''}`}>
                          {product.stock}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${product.status}`}>
                        {product.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="action-icon edit"
                        onClick={() => handleEdit(product)}
                        title="Modifier"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        className="action-icon delete"
                        onClick={() => handleDelete(product.id)}
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">
                    <div className="no-products">
                      <p>Aucun produit trouvé</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <div className="card-header">
                <div className="product-type-info">
                  <div className={`type-icon ${product.type}`}>
                    {product.type === 'product' ? <Package size={16} /> : <Tag size={16} />}
                  </div>
                  <div className="product-details">
                    <span className="product-sku">SKU: {product.sku}</span>
                    <span className={`type-badge ${product.type}`}>
                      {product.type === 'product' ? 'Produit' : 'Service'}
                    </span>
                  </div>
                </div>
                <div className="card-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => handleEdit(product)}
                    title="Modifier"
                  >
                    <Edit size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(product.id)}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className="card-content">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">{product.description}</p>
                
                <div className="product-details-list">
                  <div className="detail-item">
                    <Tag size={14} />
                    <span>Catégorie: {product.category}</span>
                  </div>
                  <div className="detail-item">
                    <DollarSign size={14} />
                    <span>Prix: {formatDZD(product.price)}/{product.unit}</span>
                  </div>
                  {product.type === 'product' && (
                    <div className="detail-item">
                      <Package size={14} />
                      <span className={`stock ${product.stock <= 5 ? 'low' : ''}`}>
                        Stock: {product.stock}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="product-stats">
                <div className="stat">
                  <span className="stat-value">{formatDZD(product.price)}</span>
                  <span className="stat-label">Prix</span>
                </div>
                {product.type === 'product' ? (
                  <div className="stat">
                    <span className="stat-value">{product.stock}</span>
                    <span className="stat-label">En stock</span>
                  </div>
                ) : (
                  <div className="stat">
                    <span className="stat-value">{product.unit}</span>
                    <span className="stat-label">Unité</span>
                  </div>
                )}
              </div>
              
              <div className="product-footer">
                <span className="product-category">{product.category}</span>
                <span className={`product-status ${product.status}`}>
                  {product.status === 'active' ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Modifier le produit/service' : 'Nouveau produit/service'}</h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nom du produit/service"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                  >
                    <option value="product">Produit</option>
                    <option value="service">Service</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description détaillée..."
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Catégorie *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    required
                    className="category-dropdown"
                  >
                    <option value="">Sélectionnez une catégorie</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    placeholder="Code produit (auto si vide)"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Prix (DZD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Unité *</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    required
                  >
                    {units.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                {formData.type === 'product' && (
                  <div className="form-group">
                    <label>Stock initial</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock}
                      onChange={(e) => setFormData({...formData, stock: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProduct ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products; 